/**
 * URDF export: the result/progress callbacks, the download fallback, and the
 * send wrapper that has to know whether a callback is waiting.
 *
 * Split out of WebSocket.tsx because it is the one part of the provider that
 * carries state shared beyond a single provider instance.
 */

import { useRef, useCallback } from "react";
import * as msg from "./protocol.senders.generated";
import type { Message, ExportUrdfResultMessage, ExportUrdfProgressMessage, MeshFormat } from "./types";

/**
 * Shared by every WebSocketProvider mounted in the page, so that two of them
 * receiving the same export_urdf_result do not both act on it. Deliberately a
 * single named object rather than loose `let`s: at each use site it is then
 * visible that this is state outside the provider's own lifetime.
 *
 * Whether two providers are ever live at once was traced, and the answer is
 * no, on every path found:
 *
 *   - The routes that mount one are exclusive. /builder/:sessionId and
 *     /configure render one at a time, and ConfigurePage hands ExportStep the
 *     sendExportUrdf and setOnExportUrdfResult of its own provider, so one
 *     provider both asks for the export and receives the result.
 *   - StrictMode's double mount does not reach it either: the connect effect
 *     closes the socket on cleanup, so the discarded instance is long gone by
 *     the time a result arrives.
 *   - Two browser tabs do reach the same session, but they are separate JS
 *     contexts and this object is per-context, so it cannot help there.
 *
 * It is kept anyway, as insurance against a duplicate download. The cost of
 * keeping it: while a filename sits in `inProgress` -- one second on the
 * download path, five on the callback path -- a second result for that same
 * filename is dropped without a word. Re-exporting under an unchanged package
 * name inside that window looks like nothing happened.
 *
 * urdfExport.test.ts pins all of this down, including the two-hook case that
 * the analysis above says is unreachable.
 */
const exportCoordination = {
  /** Filename currently being handled, so a second provider skips it. */
  inProgress: null as string | null,
  /** A provider holding a result callback has asked for this export. */
  expectsCallback: false,
};

export function useUrdfExport(send: (message: Message) => void) {
  const exportUrdfCallbackRef = useRef<((result: ExportUrdfResultMessage) => void) | null>(null);
  const exportUrdfProgressCallbackRef = useRef<((progress: ExportUrdfProgressMessage) => void) | null>(null);

  const handleExportProgress = (message: ExportUrdfProgressMessage) => {
    exportUrdfProgressCallbackRef.current?.(message);
  };

  const handleExportResult = (message: ExportUrdfResultMessage) => {
    const filename = message.filename || "export";
    console.log("[WebSocket] handleExportResult called, callback:", !!exportUrdfCallbackRef.current, "exportCoordination.inProgress:", exportCoordination.inProgress, "expectsCallback:", exportCoordination.expectsCallback, "filename:", filename);

    // Check if this export is already being handled globally
    if (exportCoordination.inProgress === filename) {
      console.log("[WebSocket] Export already in progress globally, skipping");
      return;
    }

    // If a callback is registered, use it instead of default behavior
    if (exportUrdfCallbackRef.current) {
      console.log("[WebSocket] Calling registered callback");
      exportCoordination.inProgress = filename;
      exportCoordination.expectsCallback = false; // Reset the flag
      exportUrdfCallbackRef.current(message);
      // Clear after a delay to allow for async processing
      setTimeout(() => {
        if (exportCoordination.inProgress === filename) {
          exportCoordination.inProgress = null;
        }
      }, 5000);
      return;
    }

    // If another provider is expecting to handle this with a callback, skip default behavior
    if (exportCoordination.expectsCallback) {
      console.log("[WebSocket] Another provider expects callback, skipping default download");
      return;
    }

    // No callback expected, do default download
    console.log("[WebSocket] No callback, using default download behavior");
    if (message.success && message.data && message.filename) {
      exportCoordination.inProgress = filename;

      // Decode base64 and trigger download
      const byteCharacters = atob(message.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/zip" });

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = message.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      console.log(`Downloaded: ${message.filename}`);

      setTimeout(() => {
        if (exportCoordination.inProgress === filename) {
          exportCoordination.inProgress = null;
        }
      }, 1000);
    } else if (message.error) {
      console.error(`Export failed: ${message.error}`);
      alert(`Export failed: ${message.error}`);
    }
  };

  const sendExportUrdf = useCallback(
    (
      packageName: string,
      visualMeshFormat: MeshFormat = "glb",
      collisionMeshFormat: MeshFormat = "glb"
    ) => {
      // If this provider has a callback, mark that we expect callback handling
      if (exportUrdfCallbackRef.current) {
        exportCoordination.expectsCallback = true;
        console.log("[WebSocket] sendExportUrdf - callback registered, setting exportCoordination.expectsCallback=true");
      }
      send(
        msg.exportUrdf({
          package_name: packageName,
          visual_mesh_format: visualMeshFormat,
          collision_mesh_format: collisionMeshFormat,
        })
      );
    },
    [send]
  );

  const setOnExportUrdfResult = useCallback(
    (callback: ((result: ExportUrdfResultMessage) => void) | null) => {
      console.log("[WebSocket] setOnExportUrdfResult called, callback:", callback ? "function" : "null");
      exportUrdfCallbackRef.current = callback;
    },
    []
  );

  const setOnExportUrdfProgress = useCallback(
    (callback: ((progress: ExportUrdfProgressMessage) => void) | null) => {
      exportUrdfProgressCallbackRef.current = callback;
    },
    []
  );

  return {
    sendExportUrdf,
    setOnExportUrdfResult,
    setOnExportUrdfProgress,
    handleExportResult,
    handleExportProgress,
    onExportUrdfResult: exportUrdfCallbackRef.current,
  };
}
